import React from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useRouteChangeHandler } from 'shared/hooks';
import { useInfoStore } from 'store/useInfoStore';

import { NavList } from '../constants';
import AppHeaderItem from './AppHeaderItem';
import AuthButtons from './AuthButtons';
import HamburgerMenu from './HamburgerMenu';
import LanguageSwitcher from './LanguageSwitcher';
import ServiceIcons from './ServiceIcons';

const AppHeader = () => {
  const { t } = useTranslation();
  const { handleRouteChange } = useRouteChangeHandler();
  const { accessToken, memberRole } = useInfoStore();

  const { pathname, search } = useLocation();

  const isLogin = !!accessToken;

  const translatedNavList = NavList.map((item) => ({
    ...item,
    title: t(item.titleKey),
    showNewBadge: item.showNewBadge,
    subMenu:
      item.subMenu === 'services'
        ? ('services' as const)
        : item.subMenu?.map((subItem) => ({
            ...subItem,
            title: t(subItem.titleKey),
          })),
  }));

  return (
    <header className="sticky top-0 z-30 flex h-[60px] w-full items-center justify-center bg-white md:h-[99px]">
      <div className="mx-auto flex w-full max-w-[1480px] items-center justify-between px-4 py-3 md:px-[60px] md:py-[30px]">
        <a
          href={`${pathname}${search}#footer-content`}
          className="skip-link fixed left-0 right-0 top-0 z-[100] -translate-y-full bg-black p-2 text-center text-white outline-none transition-transform duration-150 focus:translate-y-0"
        >
          Skip to main footer
        </a>

        {/* Left side - Logo and Navigation */}
        <div className="flex flex-1 items-center gap-[60px]">
          <button
            onClick={() => {
              window.dataLayer?.push({
                event: 'click_menu',
                menu: 'logo',
                section: 'Header',
              });
              handleRouteChange('/');
            }}
          >
            <img
              src="/images/optimized/checkmate_logo.webp"
              alt="CheckmateKorea"
              className="h-6 w-auto cursor-pointer md:h-8"
            />
          </button>

          <nav className="hidden items-center gap-3 md:flex">
            {translatedNavList.map(({ title, to, target, subMenu, showNewBadge, gaLabel }, idx) => (
              <AppHeaderItem
                key={idx}
                title={title}
                to={to}
                target={target}
                subMenu={subMenu}
                showNewBadge={showNewBadge}
                gaLabel={gaLabel}
                memberRole={memberRole}
              />
            ))}
          </nav>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-1">
          {!isLogin ? (
            <>
              <div className="hidden md:flex md:items-center md:gap-3">
                <AuthButtons />
              </div>
              <LanguageSwitcher />
              <HamburgerMenu isLogin={false} />
            </>
          ) : (
            <>
              <ServiceIcons />
              <LanguageSwitcher />
              <HamburgerMenu isLogin={true} />
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
